// Semaine, menus & recettes — « un réservoir par repas ».
//
// Une seule source de vérité, sans seconde copie du cycle :
//   - le réservoir d'un repas = editorial_overrides.meals[slot]
//     ({ title, hint?, alternatives: [{ title, hint? }] }) + les recettes
//     consultation.meal_recipes[mealKey(slot, title)] ;
//   - les 4 semaines = editorial_overrides.four_week_rotation (format existant) :
//     la semaine k désigne quel choix du réservoir est le plat principal,
//     les autres choix restent les alternatives de remplacement.
//
// Toutes les fonctions sont pures : elles renvoient la consultation suivante,
// l'enregistrement reste fait par l'appelant (chemin confirmé existant).
// Aucune recette ni aucun plat n'est inventé ici : on déplace, copie ou
// enregistre uniquement ce qu'Anissa a écrit.

import { buildClientAppPlanFromConsultation } from "./clientAppMapper";
import { mealKey } from "./extractMealsFromPlan";
import { sha256HexSync } from "./sha256Sync";
import {
  MISSING_CHOICE_ID,
  buildFourWeekRotationSourceRevision,
  getFourWeekRotationPublicationIssue,
  parseFourWeekRotationOverride,
} from "./fourWeekRotation";
import { formatStructuredIngredient, parseLegacyIngredientLine } from "./structuredRecipe";
import { findExclusionTermsPresent } from "./clinicalClearance";
import {
  analyzeFoodExclusions,
  FOOD_CONFLICT_ACTIONS,
  explicitFoodExclusions,
  findMenuFoodConflicts,
  matchingExclusions,
  menuTextLines,
  uninterpretableExclusionMessage,
} from "./explicitFoodGuard";

export const WEEK_MENUS_MIN_ALTERNATIVES = 3;
export const WEEK_MENUS_WEEK_COUNT = 4;
export const WEEK_MENUS_REVIEW_VERSION = 1;

// Limites alignées sur normalizeRecipe de fourWeekRotation : une recette
// « complète » ici est une recette que la rotation acceptera.
export const WEEK_MENUS_LIMITS = Object.freeze({
  title: 180,
  minTitle: 3,
  hint: 300,
  ingredients: 80,
  ingredientLength: 300,
  steps: 80,
  stepLength: 500,
  tip: 500,
});

const SLOT_ORDER = [
  "breakfast",
  "morning_snack",
  "lunch",
  "afternoon_snack",
  "dinner",
  "evening_snack",
];

// Appellations suisses : dîner = midi, souper = soir.
export const WEEK_MENU_SLOT_LABELS = Object.freeze({
  breakfast: "Petit-déjeuner",
  morning_snack: "Collation du matin",
  lunch: "Dîner",
  afternoon_snack: "Collation de l’après-midi",
  dinner: "Souper",
  evening_snack: "Collation du soir",
});

export const WEEK_MENU_STATUS = Object.freeze({
  INCOMPLETE: "incomplete",
  SAVED_TO_VALIDATE: "saved_to_validate",
  COMPLETE_TO_VALIDATE: "complete_to_validate",
  VALIDATED: "validated",
});

export const WEEK_MENU_STATUS_LABELS = Object.freeze({
  incomplete: "Incomplet",
  saved_to_validate: "Modifications enregistrées — à valider",
  complete_to_validate: "Complet — à valider",
  validated: "Validé",
});

export const WEEK_ROTATION_STATUS = Object.freeze({
  PROPOSAL: "proposal",
  DRAFT: "draft",
  TO_REFRESH: "to_refresh",
  VALIDATED: "validated",
  INVALID: "invalid",
});

export const WEEK_ROTATION_STATUS_LABELS = Object.freeze({
  proposal: "Proposition — à valider",
  draft: "Brouillon — à valider",
  to_refresh: "À actualiser",
  validated: "Validée",
  invalid: "À vérifier",
});

// ─── Outils ──────────────────────────────────────────────────────────────

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.keys(value).sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, stableValue(value[key])]),
  );
}

function hashOf(value) {
  return sha256HexSync(JSON.stringify(stableValue(value)));
}

function cloneJson(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function oneLine(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

function textLines(value) {
  const raw = typeof value === "string"
    ? value.split(/\r?\n/)
    : (Array.isArray(value) ? value : []);
  return raw.map((line) => (typeof line === "string" ? line.trim() : "")).filter(Boolean);
}

/** Même slug que le mapper : les identifiants d'alternatives restent identiques. */
function slugify(text) {
  return String(text || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50) || "x";
}

function uniqueId(base, used) {
  let id = base;
  let n = 1;
  while (used.has(id)) id = `${base}-${n++}`;
  used.add(id);
  return id;
}

/**
 * Identifiants que le mapper donnera aux alternatives d'un override.
 * « primary » est réservé au plat principal (même règle que le mapper).
 */
export function alternativeIdsForTitles(titles) {
  const used = new Set(["primary"]);
  return titles.map((title) => {
    const clean = typeof title === "string" ? title.trim() : "";
    if (!clean || clean.length < WEEK_MENUS_LIMITS.minTitle) return null;
    return uniqueId(slugify(clean), used);
  });
}

/**
 * Titre lisible tiré d'une ligne brute du plan (« 120 à 130 g de truite +
 * 180 à 200 g de panais… + 1 c.s. huile d'olive » → « Truite, panais…, huile
 * d'olive ») : quantités, unités et « + » retirés. Proposé à Anissa dans
 * l'éditeur, jamais appliqué en silence (le titre fait la clé de la recette).
 * Renvoie le titre tel quel s'il ne contient aucune quantité.
 */
export function readableMealTitle(title) {
  const text = typeof title === "string" ? title.replace(/\s+/g, " ").trim() : "";
  if (!text) return text;
  const parts = text.split(/\s*\+\s*/).filter(Boolean);
  let changed = false;
  const labels = parts.map((part) => {
    const label = parseLegacyIngredientLine(part)?.label;
    if (label && label !== part) {
      changed = true;
      return label;
    }
    return part;
  });
  if (!changed) return text;
  const joined = labels.join(", ");
  return joined.charAt(0).toLocaleUpperCase("fr") + joined.slice(1);
}

export function weekMenuSlotLabel(slot) {
  return WEEK_MENU_SLOT_LABELS[slot] || String(slot || "Repas");
}

function lowerLabel(slot) {
  const label = weekMenuSlotLabel(slot);
  return label.charAt(0).toLocaleLowerCase("fr") + label.slice(1);
}

function recipeHasContent(recipe) {
  return Boolean(recipe && typeof recipe === "object" && !Array.isArray(recipe)
    && ((Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0)
      || (Array.isArray(recipe.preparation) && recipe.preparation.length > 0)
      || (Array.isArray(recipe.ingredients_structured) && recipe.ingredients_structured.length > 0)));
}

function recipeIngredientLines(recipe) {
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) {
    return recipe.ingredients.map((line) => (typeof line === "string" ? line : "")).filter(Boolean);
  }
  if (Array.isArray(recipe?.ingredients_structured)) {
    return recipe.ingredients_structured.map((item) => formatStructuredIngredient(item)).filter(Boolean);
  }
  return [];
}

/** Vue éditable d'une recette stockée : lignes telles qu'Anissa les a écrites. */
export function editableRecipe(recipe) {
  if (!recipe || typeof recipe !== "object" || Array.isArray(recipe)) return null;
  return {
    ingredients: recipeIngredientLines(recipe),
    preparation: Array.isArray(recipe.preparation)
      ? recipe.preparation.filter((step) => typeof step === "string")
      : [],
    tip: typeof recipe.tip === "string" ? recipe.tip : "",
  };
}

function ingredientsCount(recipe) {
  return Array.isArray(recipe?.ingredients) ? recipe.ingredients.length
    : (Array.isArray(recipe?.ingredients_structured) ? recipe.ingredients_structured.length : 0);
}

/**
 * Problèmes d'une recette, au sens de la rotation 4 semaines : ingrédients ET
 * étapes non vides, dans les limites de longueur.
 */
function recipeIssues(recipe, prefix) {
  if (!recipeHasContent(recipe)) return [`${prefix} : recette manquante`];
  const issues = [];
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const steps = Array.isArray(recipe.preparation) ? recipe.preparation : [];
  if (ingredients.length === 0) issues.push(`${prefix} : ingrédients manquants`);
  if (steps.length === 0) issues.push(`${prefix} : étapes de préparation manquantes`);
  if (ingredients.length > WEEK_MENUS_LIMITS.ingredients) {
    issues.push(`${prefix} : ${ingredients.length} ingrédients, ${WEEK_MENUS_LIMITS.ingredients} au maximum`);
  }
  if (ingredients.some((line) => !oneLine(line))) issues.push(`${prefix} : une ligne d’ingrédient est vide`);
  if (ingredients.some((line) => oneLine(line).length > WEEK_MENUS_LIMITS.ingredientLength)) {
    issues.push(`${prefix} : un ingrédient dépasse ${WEEK_MENUS_LIMITS.ingredientLength} caractères`);
  }
  if (steps.length > WEEK_MENUS_LIMITS.steps) {
    issues.push(`${prefix} : ${steps.length} étapes, ${WEEK_MENUS_LIMITS.steps} au maximum`);
  }
  if (steps.some((line) => !oneLine(line))) issues.push(`${prefix} : une étape de préparation est vide`);
  if (steps.some((line) => oneLine(line).length > WEEK_MENUS_LIMITS.stepLength)) {
    issues.push(`${prefix} : une étape dépasse ${WEEK_MENUS_LIMITS.stepLength} caractères`);
  }
  if (recipe.tip !== undefined && (typeof recipe.tip !== "string" || !oneLine(recipe.tip)
      || oneLine(recipe.tip).length > WEEK_MENUS_LIMITS.tip)) {
    issues.push(`${prefix} : consigne vide ou de plus de ${WEEK_MENUS_LIMITS.tip} caractères`);
  }
  return issues;
}

export function isCompleteWeekMenuRecipe(recipe) {
  return recipeIssues(recipe, "").length === 0;
}

// Le mapper est pur : une consultation (objet immuable) donne toujours la même
// semaine. Le cache évite de le relancer à chaque rendu de l'éditeur.
const weekMealsCache = new WeakMap();

function resolveWeekMeals({ client, consultation, weekMeals }) {
  if (weekMeals) return weekMeals;
  if (!client || !consultation || typeof consultation !== "object") return null;
  const cached = weekMealsCache.get(consultation);
  if (cached && cached.client === client) return cached.weekMeals;
  let resolved = null;
  try {
    resolved = buildClientAppPlanFromConsultation(client, consultation)?.sections?.week_meals || null;
  } catch {
    resolved = null;
  }
  weekMealsCache.set(consultation, { client, weekMeals: resolved });
  return resolved;
}

function exclusionSnapshot(client) {
  const form = client?.form;
  if (!form || typeof form !== "object") return null;
  return {
    allergies: form.allergies,
    alimentsEvites: form.alimentsEvites,
    restrictionsAutre: form.restrictionsAutre,
  };
}

function choiceGuardLabel(slotModel, choice) {
  return choice.kind === "primary"
    ? `${slotModel.label}, plat principal « ${choice.title} »`
    : `${slotModel.label}, alternative ${choice.position} « ${choice.title} »`;
}

/** Aliments explicitement exclus trouvés dans les menus d'un ou de tous les repas. */
/** Empreinte de l'ensemble des exclusions comparées : la changer annule les confirmations. */
export function exclusionsSha256(snapshot) {
  // Trié : l'ordre dans lequel Anissa a écrit les exclusions n'annule rien.
  return hashOf(snapshot ? [...explicitFoodExclusions(snapshot)].sort() : []);
}

/**
 * Une confirmation vaut pour un repas (créneau), un champ, un texte exact et
 * un aliment, avec les mêmes exclusions. L'identifiant du choix n'est qu'une
 * trace : dérivé du titre, il peut glisser quand une alternative au titre
 * voisin est retirée, et ne doit pas faire perdre une confirmation valable.
 */
function sameConfirmedConflict(entry, conflict, textSha256, currentExclusionsSha256) {
  return entry?.field === conflict.field
    && entry?.text_sha256 === textSha256
    && entry?.exclusion === conflict.exclusion
    && entry?.exclusions_sha256 === currentExclusionsSha256;
}

function foodConflictKey(conflict) {
  return [conflict.choiceId, conflict.field, conflict.textSha256, conflict.exclusion].join("|");
}

/**
 * Aliments exclus trouvés dans les menus d'un ou de tous les repas. Chaque
 * conflit porte une clé stable et indique si Anissa l'a confirmé compatible
 * pour CE texte et CES exclusions (`consultation` fournit les confirmations).
 */
export function weekMenusFoodConflicts(model, snapshot, { slot = null, consultation = null } = {}) {
  if (!snapshot) return [];
  const review = consultation ? readReview(consultation) : null;
  const currentExclusionsSha256 = exclusionsSha256(snapshot);
  const slots = (model?.slots || []).filter((slotModel) => !slot || slotModel.slot === slot);
  return slots.flatMap((slotModel) => {
    const confirmations = Array.isArray(review?.slots?.[slotModel.slot]?.food_conflicts_confirmed)
      ? review.slots[slotModel.slot].food_conflicts_confirmed
      : [];
    return findMenuFoodConflicts(slotModel.choices.map((choice) => ({
      id: choice.id,
      label: choiceGuardLabel(slotModel, choice),
      title: choice.title,
      hint: choice.hint,
      recipe: choice.rawRecipe,
    })), snapshot, { action: FOOD_CONFLICT_ACTIONS.CONFIRM }).map((conflict) => {
      const textSha256 = sha256HexSync(conflict.text);
      const enriched = { ...conflict, slot: slotModel.slot, textSha256 };
      return {
        ...enriched,
        key: foodConflictKey(enriched),
        confirmed: confirmations.some((entry) => sameConfirmedConflict(entry, conflict, textSha256, currentExclusionsSha256)),
      };
    });
  });
}

/** Libellé de la case de confirmation d'un conflit (texte exact affiché). */
export function foodConflictConfirmationLabel(conflict) {
  return `Je confirme que « ${conflict.text} » est compatible avec l’exclusion « ${conflict.exclusion} »`;
}

function readReview(consultation) {
  const review = consultation?.editorial_overrides?.week_menus_review;
  if (!review || typeof review !== "object" || review.version !== WEEK_MENUS_REVIEW_VERSION) {
    return { version: WEEK_MENUS_REVIEW_VERSION, slots: {}, rotation: null };
  }
  return {
    version: WEEK_MENUS_REVIEW_VERSION,
    slots: review.slots && typeof review.slots === "object" ? review.slots : {},
    rotation: review.rotation && typeof review.rotation === "object" ? review.rotation : null,
  };
}

function slotHash(slot, choices) {
  return hashOf({
    slot,
    choices: choices.map((choice) => ({
      title: choice.title,
      hint: choice.hint || "",
      recipe: choice.rawRecipe ?? null,
    })),
  });
}

function rotationSlotHash(poolHash, mains) {
  return hashOf({ pool: poolHash, mains });
}

function joinWeeks(weeks) {
  if (weeks.length === 1) return `la semaine ${weeks[0]}`;
  return `les semaines ${weeks.slice(0, -1).join(", ")} et ${weeks[weeks.length - 1]}`;
}

// ─── Modèle ──────────────────────────────────────────────────────────────

function readSlots(weekMeals, consultation) {
  const overrides = consultation?.editorial_overrides || {};
  const overrideMeals = Array.isArray(overrides.meals) ? overrides.meals : [];
  const recipes = consultation?.meal_recipes && typeof consultation.meal_recipes === "object"
    ? consultation.meal_recipes
    : {};
  const firstBySlot = new Map();
  const mealIdsBySlot = new Map();
  const titlesBySlot = new Map();
  for (const day of Array.isArray(weekMeals?.days) ? weekMeals.days : []) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      const slot = String(meal?.slot || "").trim();
      if (!slot) continue;
      if (!firstBySlot.has(slot)) firstBySlot.set(slot, meal);
      if (!mealIdsBySlot.has(slot)) mealIdsBySlot.set(slot, []);
      if (meal?.id) mealIdsBySlot.get(slot).push(meal.id);
      if (!titlesBySlot.has(slot)) titlesBySlot.set(slot, new Set());
      titlesBySlot.get(slot).add(String(meal?.title || "").trim().toLocaleLowerCase("fr"));
    }
  }
  const ordered = [...firstBySlot.keys()].sort((a, b) => {
    const ai = SLOT_ORDER.indexOf(a);
    const bi = SLOT_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return ordered.map((slot) => {
    const meal = firstBySlot.get(slot);
    const override = overrideMeals.find((entry) => String(entry?.slot || "").trim() === slot) || null;
    const makeChoice = (id, kind, position, title, hint) => {
      const recipeKey = mealKey(slot, title);
      const rawRecipe = recipes[recipeKey] && typeof recipes[recipeKey] === "object"
        ? recipes[recipeKey]
        : null;
      return {
        id,
        kind,
        position,
        title,
        hint: hint || "",
        recipeKey,
        rawRecipe,
        recipe: editableRecipe(rawRecipe),
        hasRecipe: recipeHasContent(rawRecipe),
        recipeComplete: isCompleteWeekMenuRecipe(rawRecipe),
        ingredientCount: ingredientsCount(rawRecipe),
        stepCount: Array.isArray(rawRecipe?.preparation) ? rawRecipe.preparation.length : 0,
      };
    };
    const primaryTitle = String(meal?.title || "").trim();
    const primaryHint = typeof override?.hint === "string" ? override.hint : (meal?.hint || "");
    const choices = [makeChoice("primary", "primary", 0, primaryTitle, primaryHint)];
    (Array.isArray(meal?.alternatives) ? meal.alternatives : []).forEach((alternative, index) => {
      if (!alternative?.id || !alternative?.title) return;
      choices.push(makeChoice(
        alternative.id,
        "alternative",
        index + 1,
        String(alternative.title).trim(),
        alternative.hint || "",
      ));
    });
    const distinctDayTitles = titlesBySlot.get(slot)?.size || 0;
    return {
      slot,
      label: weekMenuSlotLabel(slot),
      hasOverride: Boolean(override),
      // Plan jour par jour (ou rotation par jour héritée) : le titre change
      // selon les jours. Le premier enregistrement imposera le même réservoir
      // aux 7 jours, ce qu'Anissa doit confirmer explicitement.
      variesByDay: distinctDayTitles > 1,
      mealIds: mealIdsBySlot.get(slot) || [],
      choices,
    };
  });
}

function slotIssues(slotModel) {
  const label = slotModel.label;
  const issues = [];
  const alternatives = slotModel.choices.filter((choice) => choice.kind === "alternative");
  if (alternatives.length < WEEK_MENUS_MIN_ALTERNATIVES) {
    issues.push(`${label} : ${alternatives.length} alternative${alternatives.length > 1 ? "s" : ""} sur ${WEEK_MENUS_MIN_ALTERNATIVES} minimum`);
  }
  for (const choice of slotModel.choices) {
    if (choice.title.length > WEEK_MENUS_LIMITS.title) {
      issues.push(`${label} — « ${choice.title.slice(0, 40)}… » : titre de plus de ${WEEK_MENUS_LIMITS.title} caractères`);
    }
    if (choice.hint && oneLine(choice.hint).length > WEEK_MENUS_LIMITS.hint) {
      issues.push(`${label} — « ${choice.title} » : description de plus de ${WEEK_MENUS_LIMITS.hint} caractères`);
    }
    if (choice.kind === "primary") {
      if (!choice.hasRecipe) {
        issues.push(`${label} : plat principal sans recette`);
      } else {
        issues.push(...recipeIssues(choice.rawRecipe, `${label} — plat principal « ${choice.title} »`));
      }
    } else {
      issues.push(...recipeIssues(choice.rawRecipe, `${label} — alternative ${choice.position} « ${choice.title} »`));
    }
  }
  return issues;
}

/**
 * Proposition par défaut : seulement parmi les choix dont la recette est
 * complète (ce sont les seuls que la rotation acceptera), sinon tous.
 */
function defaultMains(choices) {
  const complete = choices.filter((choice) => choice.recipeComplete);
  const candidates = complete.length > 0 ? complete : choices;
  return Array.from({ length: WEEK_MENUS_WEEK_COUNT }, (_, weekOffset) => (
    candidates[weekOffset % candidates.length]?.id || "primary"
  ));
}

function rotationIssuesFor(slotModel, mains) {
  const issues = [];
  const n = slotModel.choices.length;
  if (n < WEEK_MENUS_WEEK_COUNT) {
    issues.push(`${slotModel.label} : ${n} choix pour 4 semaines — ajoute des alternatives pour que chaque semaine soit différente`);
    return issues;
  }
  const missing = mains.map((id, index) => (id ? null : index + 1)).filter(Boolean);
  if (missing.length > 0) {
    issues.push(`${slotModel.label} : plat principal à choisir pour ${joinWeeks(missing)}`);
  }
  const seen = new Map();
  mains.forEach((choiceId, index) => {
    if (!choiceId) return;
    if (seen.has(choiceId)) {
      issues.push(`${slotModel.label} : les semaines ${seen.get(choiceId)} et ${index + 1} ont le même plat principal`);
    } else {
      seen.set(choiceId, index + 1);
    }
  });
  return issues;
}

function aggregateStatus(statuses) {
  if (statuses.length === 0) return WEEK_MENU_STATUS.INCOMPLETE;
  if (statuses.includes(WEEK_MENU_STATUS.INCOMPLETE)) return WEEK_MENU_STATUS.INCOMPLETE;
  if (statuses.every((status) => status === WEEK_MENU_STATUS.VALIDATED)) return WEEK_MENU_STATUS.VALIDATED;
  if (statuses.includes(WEEK_MENU_STATUS.SAVED_TO_VALIDATE)) return WEEK_MENU_STATUS.SAVED_TO_VALIDATE;
  return WEEK_MENU_STATUS.COMPLETE_TO_VALIDATE;
}

export const WEEK_MENUS_PUBLISHED_LABEL = "Contenu déjà publié — à revalider si tu le modifies";

function aggregateLabel(slots, status) {
  const neutral = status === WEEK_MENU_STATUS.COMPLETE_TO_VALIDATE
    && slots.some((slotModel) => slotModel.publishedBaseline)
    && slots.every((slotModel) => slotModel.publishedBaseline || slotModel.status === WEEK_MENU_STATUS.VALIDATED);
  return neutral ? WEEK_MENUS_PUBLISHED_LABEL : WEEK_MENU_STATUS_LABELS[status];
}

/**
 * Modèle de l'éditeur « Semaine, menus & recettes ».
 *
 * `alreadyPublished` : contenu déjà en ligne sans relecture enregistrée par
 * cet éditeur — affiché de façon neutre, pas comme une alerte.
 *
 * @param {{ consultation: object, weekMeals?: object, client?: object, alreadyPublished?: boolean }} input
 */
export function buildWeekMenusModel({ consultation, weekMeals = null, client = null, alreadyPublished = false } = {}) {
  const resolvedWeekMeals = resolveWeekMeals({ client, consultation, weekMeals });
  const review = readReview(consultation);
  const storedRotation = consultation?.editorial_overrides?.four_week_rotation ?? null;
  const parsedRotation = parseFourWeekRotationOverride(storedRotation);
  const snapshot = exclusionSnapshot(client);
  const proseExclusions = snapshot ? analyzeFoodExclusions(snapshot).uninterpretable : [];
  const proseExclusionsSha256 = hashOf(proseExclusions);
  const currentExclusionsSha256 = exclusionsSha256(snapshot);

  const slots = readSlots(resolvedWeekMeals, consultation).map((slotModel) => {
    const contentSha256 = slotHash(slotModel.slot, slotModel.choices);
    // Un aliment exclu bloque : le repas ne peut ni être complet ni validé.
    const foodConflicts = weekMenusFoodConflicts({ slots: [slotModel] }, snapshot, { consultation });
    const reviewEntry = review.slots[slotModel.slot] || null;
    // Exclusions écrites en prose : jamais devinées. Le repas reste bloqué
    // tant qu'Anissa n'a pas confirmé l'avoir vérifié, pour CE contenu et
    // CES exclusions.
    const proseChecked = proseExclusions.length === 0
      || (reviewEntry?.prose_exclusions_checked?.content_sha256 === contentSha256
        && reviewEntry?.prose_exclusions_checked?.exclusions_sha256 === proseExclusionsSha256);
    const proseIssues = proseChecked ? [] : proseExclusions.map(uninterpretableExclusionMessage);
    const issues = [
      ...slotIssues(slotModel),
      ...foodConflicts.filter((conflict) => !conflict.confirmed).map((conflict) => conflict.message),
      ...proseIssues,
    ];
    // Validé seulement pour ce contenu ET ces exclusions : une exclusion
    // ajoutée ou retirée retire la validation, même après confirmation.
    const validated = issues.length === 0 && reviewEntry?.content_sha256 === contentSha256
      && reviewEntry?.exclusions_sha256 === currentExclusionsSha256;
    const saved = reviewEntry?.saved_sha256 === contentSha256;
    const status = issues.length > 0
      ? WEEK_MENU_STATUS.INCOMPLETE
      : validated
        ? WEEK_MENU_STATUS.VALIDATED
        : saved ? WEEK_MENU_STATUS.SAVED_TO_VALIDATE : WEEK_MENU_STATUS.COMPLETE_TO_VALIDATE;
    const publishedBaseline = alreadyPublished && !reviewEntry && status === WEEK_MENU_STATUS.COMPLETE_TO_VALIDATE;
    const validChoiceIds = new Set(slotModel.choices.map((choice) => choice.id));
    const defaults = defaultMains(slotModel.choices);
    // Rotation enregistrée : un choix devenu invalide (alternative retirée)
    // reste « à choisir » ; jamais de remplacement silencieux.
    const mains = defaults.map((defaultId, weekOffset) => {
      if (!parsedRotation) return { id: defaultId, fromRotation: false, missing: false };
      const stored = parsedRotation.weeks?.[weekOffset]?.choices?.[slotModel.mealIds[0]];
      return stored && validChoiceIds.has(stored)
        ? { id: stored, fromRotation: true, missing: false }
        : { id: null, fromRotation: true, missing: true };
    });
    const mainIds = mains.map((main) => main.id);
    return {
      ...slotModel,
      contentSha256,
      rotationSha256: rotationSlotHash(contentSha256, mainIds),
      mains,
      issues,
      foodConflicts,
      proseExclusions: proseIssues.length > 0 ? proseExclusions : [],
      proseExclusionsChecked: proseExclusions.length > 0 && proseChecked,
      rotationIssues: rotationIssuesFor(slotModel, mainIds),
      status,
      publishedBaseline,
      statusLabel: publishedBaseline ? WEEK_MENUS_PUBLISHED_LABEL : WEEK_MENU_STATUS_LABELS[status],
      validatedAt: validated ? reviewEntry.validated_at || null : null,
      savedAt: saved ? reviewEntry.saved_at || null : null,
    };
  });

  const weeks = Array.from({ length: WEEK_MENUS_WEEK_COUNT }, (_, weekOffset) => {
    const mainBySlot = {};
    const fromRotation = {};
    const issues = [];
    for (const slotModel of slots) {
      const main = slotModel.mains[weekOffset];
      mainBySlot[slotModel.slot] = main.id;
      fromRotation[slotModel.slot] = main.fromRotation;
      const prefix = `Semaine ${weekOffset + 1} — ${lowerLabel(slotModel.slot)}`;
      const mainChoice = slotModel.choices.find((choice) => choice.id === main.id);
      if (main.missing) issues.push(`${prefix} : plat principal à choisir`);
      const others = slotModel.choices.filter((choice) => choice.id !== main.id);
      const complete = others.filter((choice) => choice.recipeComplete).length;
      const withoutRecipe = others.length - complete;
      const expected = Math.max(WEEK_MENUS_MIN_ALTERNATIVES, others.length);
      if (mainChoice && !mainChoice.recipeComplete) {
        issues.push(`${prefix} : plat principal « ${mainChoice.title} » sans recette complète`);
      }
      if (complete < expected) {
        const plural = complete > 1 ? "s" : "";
        issues.push(`${prefix} : ${complete} alternative${plural} complète${plural} sur ${expected}${withoutRecipe > 0 ? ` (${withoutRecipe} sans recette complète)` : ""}`);
      }
    }
    const status = aggregateStatus(slots.map((slotModel) => slotModel.status));
    return {
      index: weekOffset + 1,
      label: `Semaine ${weekOffset + 1}`,
      mainBySlot,
      fromRotation,
      isProposal: Object.values(fromRotation).some((value) => !value),
      issues,
      status,
      statusLabel: aggregateLabel(slots, status),
    };
  });

  const rotation = buildRotationState({
    slots,
    review,
    storedRotation,
    parsedRotation,
    weekMeals: resolvedWeekMeals,
  });

  const issues = slots.flatMap((slotModel) => slotModel.issues);
  const status = aggregateStatus(slots.map((slotModel) => slotModel.status));
  return {
    slots,
    weeks,
    rotation,
    issues,
    complete: slots.length > 0 && issues.length === 0,
    status,
    statusLabel: aggregateLabel(slots, status),
    publishedBaseline: slots.length > 0 && aggregateLabel(slots, status) === WEEK_MENUS_PUBLISHED_LABEL,
    weekMeals: resolvedWeekMeals,
  };
}

function rotationChanges(slots, reviewRotation) {
  if (!reviewRotation) return [];
  const pools = reviewRotation.slot_pools || {};
  const mainsBySlot = reviewRotation.slot_mains || {};
  const hashes = reviewRotation.slot_hashes || {};
  return slots.filter((slotModel) => hashes[slotModel.slot] !== slotModel.rotationSha256)
    .map((slotModel) => {
      const previousMains = Array.isArray(mainsBySlot[slotModel.slot]) ? mainsBySlot[slotModel.slot] : null;
      const poolChanged = pools[slotModel.slot] !== slotModel.contentSha256 || !previousMains;
      const weeks = poolChanged
        ? [1, 2, 3, 4]
        : slotModel.mains
          .map((main, index) => (main.id !== previousMains[index] ? index + 1 : null))
          .filter(Boolean);
      return {
        slot: slotModel.slot,
        label: slotModel.label,
        weeks: weeks.length ? weeks : [1, 2, 3, 4],
        reason: poolChanged ? "pool" : "mains",
      };
    });
}

function buildRotationState({ slots, review, storedRotation, parsedRotation, weekMeals }) {
  const issues = slots.flatMap((slotModel) => slotModel.rotationIssues);
  if (storedRotation == null) {
    return {
      status: WEEK_ROTATION_STATUS.PROPOSAL,
      statusLabel: WEEK_ROTATION_STATUS_LABELS.proposal,
      saved: false,
      changes: [],
      changedSlots: [],
      issues,
      message: "Proposition de répartition des 4 semaines, à valider.",
    };
  }
  if (!parsedRotation) {
    return {
      status: WEEK_ROTATION_STATUS.INVALID,
      statusLabel: WEEK_ROTATION_STATUS_LABELS.invalid,
      saved: true,
      changes: [],
      changedSlots: [],
      issues,
      message: "Rotation enregistrée non conforme : choisis à nouveau le plat principal de chaque semaine.",
    };
  }
  const publicationIssue = weekMeals ? getFourWeekRotationPublicationIssue(weekMeals, storedRotation) : null;
  const changes = rotationChanges(slots, review.rotation);
  let status;
  if (parsedRotation.status === "validated" && !publicationIssue && changes.length === 0) {
    status = WEEK_ROTATION_STATUS.VALIDATED;
  } else if (changes.length > 0 || review.rotation || parsedRotation.status === "validated") {
    status = WEEK_ROTATION_STATUS.TO_REFRESH;
  } else {
    status = WEEK_ROTATION_STATUS.DRAFT;
  }
  let message = null;
  if (status === WEEK_ROTATION_STATUS.TO_REFRESH) {
    message = changes.length > 0
      ? `Rotation 4 semaines à actualiser — ${changes.map((change) => `${change.label.toLocaleLowerCase("fr")} modifié (${joinWeeks(change.weeks)})`).join(", ")}. Les autres repas ne changent pas.`
      : "Rotation 4 semaines à actualiser après les modifications des menus ou recettes.";
  } else if (status === WEEK_ROTATION_STATUS.DRAFT) {
    message = "Rotation 4 semaines enregistrée, pas encore validée par Anissa.";
  }
  return {
    status,
    statusLabel: WEEK_ROTATION_STATUS_LABELS[status],
    saved: true,
    changes,
    changedSlots: changes.map((change) => change.slot),
    reviewed: Boolean(review.rotation),
    issues: publicationIssue && status !== WEEK_ROTATION_STATUS.VALIDATED && issues.length === 0
      ? [publicationIssue]
      : issues,
    message,
  };
}

/** Recettes existantes réutilisables (tous repas confondus). */
export function listReusableRecipes(model) {
  return (model?.slots || []).flatMap((slotModel) => slotModel.choices
    .filter((choice) => choice.hasRecipe)
    .map((choice) => ({
      key: choice.recipeKey,
      slot: slotModel.slot,
      title: choice.title,
      label: `${slotModel.label} · ${choice.title}`,
    })));
}

/** Repas au format paquet (principal + alternatives avec recette), pour les contrôles. */
export function buildWeekMenusPackageMeals(model) {
  return (model?.slots || []).map((slotModel) => {
    const [primary, ...alternatives] = slotModel.choices;
    const shape = (choice) => ({
      title: choice.title,
      ...(choice.hint ? { hint: choice.hint } : {}),
      recipe: choice.rawRecipe ? cloneJson(choice.rawRecipe) : null,
    });
    return {
      slot: slotModel.slot,
      slot_label: slotModel.label,
      primary: shape(primary),
      alternatives: alternatives.map(shape),
    };
  });
}

// ─── Opérations d'édition ───────────────────────────────────────────────

function fail(error) {
  return { ok: false, error };
}

function findSlot(model, slot) {
  return model.slots.find((slotModel) => slotModel.slot === slot) || null;
}

function validateTitle(title) {
  const clean = oneLine(title);
  if (!clean) return { error: "Le titre ne peut pas être vide." };
  if (clean.length < WEEK_MENUS_LIMITS.minTitle) {
    return { error: `Le titre doit contenir au moins ${WEEK_MENUS_LIMITS.minTitle} caractères.` };
  }
  if (clean.length > WEEK_MENUS_LIMITS.title) {
    return { error: `Le titre dépasse ${WEEK_MENUS_LIMITS.title} caractères.` };
  }
  return { title: clean };
}

function pendingRevision(consultation) {
  return `four-week-v1-${hashOf({
    pending: true,
    meals: consultation?.editorial_overrides?.meals || null,
    recipes: consultation?.meal_recipes || null,
  })}`;
}


function buildMealsOverride(overrides, slot, entries) {
  const existing = Array.isArray(overrides.meals) ? overrides.meals : [];
  const previous = existing.find((entry) => String(entry?.slot || "").trim() === slot) || {};
  const [primary, ...alternatives] = entries;
  // Descriptions toujours écrites explicitement (même vides) : une
  // description effacée reste effacée et un retrait ne décale rien. Le
  // marqueur `edited_in` autorise le mapper à afficher la description du
  // plat principal sans changer les programmes déjà publiés.
  const entry = {
    ...previous,
    slot,
    title: primary.title,
    hint: primary.hint || "",
    edited_in: "week_editor",
    alternatives: alternatives.map((alternative) => ({
      title: alternative.title,
      hint: alternative.hint || "",
    })),
  };
  const index = existing.findIndex((item) => String(item?.slot || "").trim() === slot);
  if (index === -1) return [...existing, entry];
  return existing.map((item, itemIndex) => (itemIndex === index ? entry : item));
}

function rotationBaseWeeks(model, parsedRotation) {
  if (parsedRotation) {
    return parsedRotation.weeks.map((week) => ({ ...week, choices: { ...week.choices } }));
  }
  return model.weeks.map((week) => ({
    id: `week-${week.index}`,
    index: week.index,
    label: week.label,
    choices: Object.fromEntries(model.slots.flatMap((slotModel) => slotModel.mealIds
      .map((mealId) => [mealId, week.mainBySlot[slotModel.slot]]))),
  }));
}

/**
 * Applique un nouveau réservoir pour un repas, déplace les recettes et met à
 * jour la rotation (si elle existe, ou si `weekMain` est demandé).
 */
function commitSlot(state, model, slotModel, entries, recipes, { weekMain = null } = {}) {
  const consultation = state.consultation || {};
  const overrides = consultation.editorial_overrides || {};
  const nextMeals = buildMealsOverride(overrides, slotModel.slot, entries);
  const altIds = alternativeIdsForTitles(entries.slice(1).map((entry) => entry.title));
  const newIds = ["primary", ...altIds];
  const idMap = new Map();
  entries.forEach((entry, index) => {
    if (entry.prevId) idMap.set(entry.prevId, newIds[index]);
  });
  const removedIds = new Set(slotModel.choices.map((choice) => choice.id).filter((id) => !idMap.has(id)));
  const lostWeeks = new Set();
  let nextConsultation = {
    ...consultation,
    meal_recipes: recipes,
    editorial_overrides: { ...overrides, meals: nextMeals },
  };

  const storedRotation = overrides.four_week_rotation ?? null;
  if (storedRotation != null || weekMain) {
    const parsed = parseFourWeekRotationOverride(storedRotation);
    const weeks = rotationBaseWeeks(model, parsed).map((week, weekOffset) => {
      const choices = { ...week.choices };
      for (const mealId of slotModel.mealIds) {
        const previous = choices[mealId];
        // Choix renommé : suivi. Choix retiré : la semaine reste « à choisir »,
        // jamais remplacée en silence.
        if (idMap.has(previous)) {
          choices[mealId] = idMap.get(previous);
        } else if (removedIds.has(previous)) {
          choices[mealId] = MISSING_CHOICE_ID;
          lostWeeks.add(weekOffset + 1);
        }
        if (weekMain && weekMain.weekIndex === weekOffset + 1) choices[mealId] = weekMain.choiceId;
      }
      return { ...week, choices };
    });
    const nextWeekMeals = resolveWeekMeals({ client: state.client, consultation: nextConsultation });
    const sourceRevision = (nextWeekMeals && buildFourWeekRotationSourceRevision(nextWeekMeals))
      || pendingRevision(nextConsultation);
    nextConsultation = {
      ...nextConsultation,
      editorial_overrides: {
        ...nextConsultation.editorial_overrides,
        four_week_rotation: {
          version: 1,
          status: "draft",
          source_revision: sourceRevision,
          repeat: true,
          weeks,
        },
      },
    };
  }
  return {
    ok: true,
    consultation: nextConsultation,
    editorial_overrides: nextConsultation.editorial_overrides,
    meal_recipes: nextConsultation.meal_recipes,
    slot: slotModel.slot,
    weeks: weekMain ? [weekMain.weekIndex] : [1, 2, 3, 4],
    lostWeeks: [...lostWeeks].sort(),
    ...(lostWeeks.size > 0 ? {
      notice: `${slotModel.label} : ${joinWeeks([...lostWeeks].sort())} n’${lostWeeks.size > 1 ? "ont" : "a"} plus de plat principal — à choisir.`,
    } : {}),
  };
}

function prepare(state, slot) {
  const model = buildWeekMenusModel(state);
  const slotModel = findSlot(model, slot);
  if (!slotModel) return { error: "Ce repas n’existe pas dans la semaine." };
  const entries = slotModel.choices.map((choice) => ({
    prevId: choice.id,
    title: choice.title,
    hint: choice.hint,
  }));
  const recipes = { ...(state.consultation?.meal_recipes || {}) };
  return { model, slotModel, entries, recipes };
}

function choiceIndex(slotModel, choiceId) {
  return slotModel.choices.findIndex((choice) => choice.id === choiceId);
}

const UNKNOWN_CHOICE = "Ce choix n’existe plus. Recharge la semaine avant de continuer.";

/** Renomme un choix et DÉPLACE sa recette vers la nouvelle clé. */
export function renameChoice(state, slot, choiceId, title) {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const index = choiceIndex(slotModel, choiceId);
  if (index === -1) return fail(UNKNOWN_CHOICE);
  const checked = validateTitle(title);
  if (checked.error) return fail(checked.error);
  const choice = slotModel.choices[index];
  if (checked.title === choice.title) return { ok: true, unchanged: true, consultation: state.consultation };
  const nextKey = mealKey(slot, checked.title);
  const duplicate = slotModel.choices.some((other, otherIndex) => otherIndex !== index
    && other.recipeKey === nextKey);
  if (duplicate) {
    return fail(`« ${checked.title} » existe déjà pour le ${lowerLabel(slot)}. Choisis un autre titre.`);
  }
  if (nextKey !== choice.recipeKey && Object.hasOwn(recipes, choice.recipeKey)) {
    recipes[nextKey] = recipes[choice.recipeKey];
    delete recipes[choice.recipeKey];
  }
  entries[index] = { ...entries[index], title: checked.title };
  return commitSlot(state, model, slotModel, entries, recipes);
}

export function setHint(state, slot, choiceId, hint) {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const index = choiceIndex(slotModel, choiceId);
  if (index === -1) return fail(UNKNOWN_CHOICE);
  const clean = oneLine(hint);
  if (clean.length > WEEK_MENUS_LIMITS.hint) {
    return fail(`La description dépasse ${WEEK_MENUS_LIMITS.hint} caractères.`);
  }
  if (clean === (slotModel.choices[index].hint || "")) {
    return { ok: true, unchanged: true, consultation: state.consultation };
  }
  entries[index] = { ...entries[index], hint: clean };
  return commitSlot(state, model, slotModel, entries, recipes);
}

/** Ajoute une alternative ; une recette vide est permise et reste éditable. */
export function addAlternative(state, slot, title, hint = "") {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const checked = validateTitle(title);
  if (checked.error) return fail(checked.error);
  const nextKey = mealKey(slot, checked.title);
  if (slotModel.choices.some((choice) => choice.recipeKey === nextKey)) {
    return fail(`« ${checked.title} » existe déjà pour le ${lowerLabel(slot)}. Choisis un autre titre.`);
  }
  const cleanHint = oneLine(hint);
  if (cleanHint.length > WEEK_MENUS_LIMITS.hint) {
    return fail(`La description dépasse ${WEEK_MENUS_LIMITS.hint} caractères.`);
  }
  entries.push({ prevId: null, title: checked.title, hint: cleanHint });
  const result = commitSlot(state, model, slotModel, entries, recipes);
  if (!result.ok) return result;
  return { ...result, choiceId: alternativeIdsForTitles(entries.slice(1).map((entry) => entry.title)).at(-1) };
}

/**
 * Retire une alternative du réservoir. Sa recette est conservée (rien n'est
 * supprimé) : la retaper en ajoutant le même titre la retrouve.
 */
export function removeAlternative(state, slot, choiceId) {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const index = choiceIndex(slotModel, choiceId);
  if (index === -1) return fail(UNKNOWN_CHOICE);
  if (index === 0) return fail("Le plat principal ne peut pas être retiré : modifie-le plutôt.");
  if (entries.length <= 2) {
    return fail("Garde au moins une alternative : remplace-la plutôt que de la retirer.");
  }
  entries.splice(index, 1);
  return commitSlot(state, model, slotModel, entries, recipes);
}

/**
 * Enregistre la recette d'un choix telle qu'Anissa l'a tapée. Les lignes
 * d'ingrédients gardent leur quantité et leur unité. Les autres champs déjà
 * présents (temps, portions…) sont conservés ; la version structurée des
 * ingrédients n'est conservée que si les lignes n'ont pas changé.
 */
export function setRecipe(state, slot, choiceId, { ingredients, preparation, tip } = {}) {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const index = choiceIndex(slotModel, choiceId);
  if (index === -1) return fail(UNKNOWN_CHOICE);
  const choice = slotModel.choices[index];
  const nextIngredients = textLines(ingredients);
  const nextSteps = textLines(preparation);
  const nextTip = typeof tip === "string" ? tip.trim() : "";
  const previous = choice.rawRecipe ? cloneJson(choice.rawRecipe) : {};
  const previousLines = recipeIngredientLines(previous);
  const next = { ...previous, ingredients: nextIngredients, preparation: nextSteps };
  if (JSON.stringify(previousLines) !== JSON.stringify(nextIngredients)) {
    // Les lignes font foi : une version structurée périmée tromperait le
    // contrôle des aliments exclus et le calcul des courses.
    delete next.ingredients_structured;
  }
  if (nextTip) next.tip = nextTip;
  else delete next.tip;
  if (nextIngredients.length === 0 && nextSteps.length === 0 && !nextTip) {
    delete recipes[choice.recipeKey];
  } else {
    recipes[choice.recipeKey] = next;
  }
  if (JSON.stringify(stableValue(recipes[choice.recipeKey] ?? null))
      === JSON.stringify(stableValue(choice.rawRecipe ?? null))) {
    return { ok: true, unchanged: true, consultation: state.consultation };
  }
  return commitSlot(state, model, slotModel, entries, recipes);
}

/** Copie une recette existante (n'importe quel repas) sur un choix. */
export function reuseRecipe(state, slot, choiceId, sourceRecipeKey) {
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  const index = choiceIndex(slotModel, choiceId);
  if (index === -1) return fail(UNKNOWN_CHOICE);
  const source = recipes[sourceRecipeKey];
  if (!recipeHasContent(source)) return fail("Cette recette est vide ou n’existe plus.");
  const targetKey = slotModel.choices[index].recipeKey;
  if (targetKey === sourceRecipeKey) return { ok: true, unchanged: true, consultation: state.consultation };
  recipes[targetKey] = cloneJson(source);
  return commitSlot(state, model, slotModel, entries, recipes);
}

/**
 * Choisit le plat principal d'un repas pour une semaine. Avec un réservoir
 * partagé, le choix s'applique à tous les jours de ce repas cette semaine-là.
 */
export function setWeekMain(state, weekIndex, slot, choiceId) {
  if (!Number.isInteger(weekIndex) || weekIndex < 1 || weekIndex > WEEK_MENUS_WEEK_COUNT) {
    return fail("Semaine inconnue.");
  }
  const prepared = prepare(state, slot);
  if (prepared.error) return fail(prepared.error);
  const { model, slotModel, entries, recipes } = prepared;
  if (choiceIndex(slotModel, choiceId) === -1) return fail(UNKNOWN_CHOICE);
  return commitSlot(state, model, slotModel, entries, recipes, {
    weekMain: { weekIndex, choiceId },
  });
}

const MERGE_KEY_LABELS = {
  meals: "les repas et alternatives",
  four_week_rotation: "la rotation 4 semaines",
  week_menus_review: "les validations des repas",
};

function sameJson(a, b) {
  return JSON.stringify(stableValue(a ?? null)) === JSON.stringify(stableValue(b ?? null));
}

/**
 * Fusion à trois branches quand une autre section a été enregistrée pendant
 * qu'Anissa modifiait les menus : `base` = version de départ du brouillon,
 * `draft` = ses modifications, `incoming` = version enregistrée reçue.
 * Seul ce que le brouillon a changé est repris. Si les deux côtés ont changé
 * la même chose différemment, la version enregistrée est gardée et le
 * conflit est signalé : aucun gagnant choisi en silence.
 */
export function mergeWeekMenusDraft(base, draft, incoming) {
  const conflicts = [];
  const pick = (baseValue, draftValue, incomingValue, label) => {
    if (sameJson(baseValue, draftValue)) return { value: incomingValue, keep: false };
    if (sameJson(baseValue, incomingValue) || sameJson(draftValue, incomingValue)) {
      return { value: draftValue, keep: true };
    }
    conflicts.push(`Conflit sur ${label} : modifié aussi ailleurs — ta version n’a pas été reprise, refais ce changement si besoin.`);
    return { value: incomingValue, keep: false };
  };
  const overrides = { ...(incoming?.editorial_overrides || {}) };
  for (const key of Object.keys(MERGE_KEY_LABELS)) {
    const { value } = pick(
      base?.editorial_overrides?.[key],
      draft?.editorial_overrides?.[key],
      incoming?.editorial_overrides?.[key],
      MERGE_KEY_LABELS[key],
    );
    if (value === undefined) delete overrides[key];
    else overrides[key] = value;
  }
  const recipes = { ...(incoming?.meal_recipes || {}) };
  const recipeKeys = new Set([
    ...Object.keys(base?.meal_recipes || {}),
    ...Object.keys(draft?.meal_recipes || {}),
    ...Object.keys(incoming?.meal_recipes || {}),
  ]);
  for (const key of recipeKeys) {
    const title = key.split("::").slice(1).join("::");
    const { value } = pick(
      base?.meal_recipes?.[key],
      draft?.meal_recipes?.[key],
      incoming?.meal_recipes?.[key],
      `la recette « ${title} »`,
    );
    if (value === undefined) delete recipes[key];
    else recipes[key] = value;
  }
  return {
    consultation: { ...incoming, meal_recipes: recipes, editorial_overrides: overrides },
    conflicts,
  };
}

/** Applique une suite d'opérations ; s'arrête à la première erreur. */
export function runWeekMenusOps(state, operations) {
  let consultation = state.consultation;
  const notices = [];
  for (const operation of operations) {
    const result = operation({ ...state, consultation });
    if (!result?.ok) return result;
    if (result.notice) notices.push(result.notice);
    consultation = result.consultation;
  }
  return { ok: true, consultation, notices };
}

// ─── Relecture ───────────────────────────────────────────────────────────

function withReview(consultation, review) {
  return {
    ...consultation,
    editorial_overrides: {
      ...(consultation?.editorial_overrides || {}),
      week_menus_review: review,
    },
  };
}

/** Valide un repas : refusé tant que le réservoir est incomplet. */
export const AUTOMATIC_CHECK_NOTE = "Contrôle automatique : correspondance littérale des aliments exclus uniquement (pas de synonymes)";

const CONSTRAINT_FIELDS = [
  ["allergies", "Allergies"],
  ["alimentsEvites", "Aliments évités"],
  ["restrictionsAutre", "Autres restrictions"],
  ["restrictionsAlimentaires", "Restrictions alimentaires"],
  ["grossesseActuelle", "Grossesse"],
  ["allaitement", "Allaitement"],
  ["digestifDiagnostic", "Pathologies digestives"],
  ["presentationSansChiffres", "Présentation sans chiffres"],
];

function constraintValue(value) {
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item ?? "").trim()).filter(Boolean);
    return items.length ? items.join(", ") : "";
  }
  if (value === true) return "Oui";
  if (value === false) return "Non";
  return String(value ?? "").trim();
}

/**
 * Contraintes de la cliente telles qu'écrites dans l'anamnèse, pour lecture
 * seule à côté de la validation d'un repas. Aucune règle n'en est tirée ici :
 * les décisions cliniques appartiennent à Anissa.
 */
export function describeClientConstraints(client) {
  const form = client?.form && typeof client.form === "object" ? client.form : {};
  return CONSTRAINT_FIELDS.map(([key, label]) => ({
    key,
    label,
    value: constraintValue(form[key]) || "Non renseigné",
  }));
}

/**
 * Anissa confirme qu'UN conflit signalé par précaution est compatible
 * (ex. « Pain sans gluten » face à l'exclusion « gluten »). La confirmation
 * est tracée dans week_menus_review, liée au texte exact, au champ, au choix
 * et à l'ensemble des exclusions : tout changement l'annule. Ce n'est jamais
 * une validation du repas.
 *
 * Le journal clinical_overrides_audit n'accepte que 4 portes (contrainte
 * CHECK de la migration V97.28) : cette trace reste donc dans la revue.
 */
export function confirmFoodConflict(state, slot, conflictKey, { now = new Date().toISOString() } = {}) {
  const snapshot = exclusionSnapshot(state.client);
  if (!snapshot) return fail("Les exclusions de la cliente sont indisponibles.");
  const model = buildWeekMenusModel(state);
  const slotModel = findSlot(model, slot);
  if (!slotModel) return fail("Ce repas n’existe pas dans la semaine.");
  const conflict = slotModel.foodConflicts.find((item) => item.key === conflictKey);
  if (!conflict) return fail("Ce conflit n’existe plus : le texte ou les exclusions ont changé.");
  if (conflict.confirmed) return { ok: true, unchanged: true, consultation: state.consultation };
  const review = readReview(state.consultation);
  const previous = Array.isArray(review.slots[slot]?.food_conflicts_confirmed)
    ? review.slots[slot].food_conflicts_confirmed
    : [];
  return {
    ok: true,
    consultation: withReview(state.consultation, {
      ...review,
      slots: {
        ...review.slots,
        [slot]: {
          ...(review.slots[slot] || {}),
          food_conflicts_confirmed: [
            ...previous,
            {
              choice_id: conflict.choiceId,
              field: conflict.field,
              text_sha256: conflict.textSha256,
              exclusion: conflict.exclusion,
              exclusions_sha256: exclusionsSha256(snapshot),
              confirmed_at: now,
            },
          ],
        },
      },
    }),
  };
}

// ─── Publication (clients MGD) ──────────────────────────────────────────

// Tout texte du livrable est lu, sauf les champs techniques ci-dessous
// (identifiants, codes, unités) — relevés sur la sortie du mapper : menus,
// recettes structurées, rotation, Meal Prep v1/v2, liste de courses, frigo.
// Un champ inconnu est donc lu par défaut (fail-closed).
const TECHNICAL_KEYS = new Set([
  "id", "slot", "unit", "category", "choices", "food_key", "foodKey",
  "preferred_day", "preferred_days", "meal_days", "serves_days", "date", "day",
  "version", "status", "source_revision", "kind", "type", "locale", "mode",
  "expected_slots", "mapped_slots", "missing_by_day", "unexpected_by_day",
]);

function isTechnicalKey(key) {
  return TECHNICAL_KEYS.has(key) || /_id$|Id$|_ids$/.test(key);
}

function readableFoodLines(value, { slot = null, path = [], key = null } = {}) {
  if (value == null) return [];
  if (typeof value === "string") {
    return key && isTechnicalKey(key) ? [] : menuTextLines(value).map((text) => ({ text, slot }));
  }
  // Tableaux mixtes (textes et objets) : chaque élément est lu.
  if (Array.isArray(value)) return value.flatMap((item) => readableFoodLines(item, { slot, path, key }));
  if (typeof value !== "object") return [];
  // Un repas (objet portant son créneau) rattache ses textes à ce repas.
  const currentSlot = typeof value.slot === "string" && typeof value.title === "string" ? value.slot : slot;
  return Object.entries(value).flatMap(([childKey, item]) => {
    // La liste « À limiter » du frigo nomme justement ce qu'il faut éviter.
    if (path.at(-1) === "fridge_data" && childKey === "limit") return [];
    if (isTechnicalKey(childKey)) return [];
    return readableFoodLines(item, { slot: currentSlot, path: [...path, childKey], key: childKey });
  });
}

function allStrings(value) {
  if (value == null) return [];
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(allStrings);
  if (typeof value === "object") return Object.values(value).flatMap(allStrings);
  return [];
}

/**
 * Conflits actuellement confirmés par Anissa, recalculés sur les menus
 * enregistrés : une confirmation d'un choix supprimé, d'un autre texte ou
 * d'un autre ensemble d'exclusions ne compte jamais.
 */
function currentConfirmedConflicts(consultation, snapshot) {
  const review = readReview(consultation);
  const currentExclusionsSha256 = exclusionsSha256(snapshot);
  const recipes = consultation?.meal_recipes || {};
  const confirmed = [];
  for (const entry of Array.isArray(consultation?.editorial_overrides?.meals) ? consultation.editorial_overrides.meals : []) {
    const slot = String(entry?.slot || "").trim();
    if (!slot || typeof entry?.title !== "string") continue;
    const alternatives = Array.isArray(entry.alternatives) ? entry.alternatives : [];
    const altIds = alternativeIdsForTitles(alternatives.map((alt) => alt?.title));
    const choices = [
      { id: "primary", label: slot, title: entry.title, hint: entry.hint, recipe: recipes[mealKey(slot, entry.title)] },
      ...alternatives.map((alt, index) => ({
        id: altIds[index],
        label: slot,
        title: alt?.title,
        hint: alt?.hint,
        recipe: recipes[mealKey(slot, alt?.title)],
      })).filter((choice) => choice.id),
    ];
    const confirmations = Array.isArray(review.slots?.[slot]?.food_conflicts_confirmed)
      ? review.slots[slot].food_conflicts_confirmed
      : [];
    for (const conflict of findMenuFoodConflicts(choices, snapshot)) {
      const textSha256 = sha256HexSync(conflict.text);
      if (confirmations.some((item) => sameConfirmedConflict(item, conflict, textSha256, currentExclusionsSha256))) {
        confirmed.push({ slot, exclusion: conflict.exclusion, textSha256 });
      }
    }
  }
  return confirmed;
}

const NARRATIVE_SECTIONS = [
  ["Le titre", (payload) => payload?.title],
  ["L’objectif", (payload) => payload?.objective],
  ["L’introduction", (payload) => payload?.sections?.intro_data],
  ["La stratégie", (payload) => payload?.sections?.strategy_data],
  ["Les compléments", (payload) => payload?.sections?.protocols_data],
  ["Le parcours", (payload) => payload?.journey_phases],
];

function narrativeReviewText(payload) {
  return NARRATIVE_SECTIONS
    .map(([name, read]) => [name, menuTextLines(allStrings(read(payload)))])
    .filter(([, lines]) => lines.length > 0)
    .map(([name, lines]) => `${name}\n${lines.join("\n")}`)
    .join("\n\n");
}

export function narrativeProseReviewLabel(fragments) {
  return `J’ai relu l’introduction, la stratégie et les compléments au regard de : « ${fragments.join(" », « ")} »`;
}

/**
 * Exclusions écrites en prose face aux textes rédigés (titre, objectif,
 * introduction, stratégie, compléments, parcours) : le code ne les interprète
 * pas, Anissa relit elle-même. La confirmation vaut pour ces textes exacts et
 * ces exclusions exactes ; ce n'est la validation de rien d'autre.
 */
export function narrativeProseReviewState({ client, consultation, payload }) {
  const snapshot = exclusionSnapshot(client);
  const fragments = snapshot ? analyzeFoodExclusions(snapshot).uninterpretable : [];
  const text = narrativeReviewText(payload);
  const textsSha256 = sha256HexSync(text);
  const exclusionsSha = hashOf([...fragments].sort());
  const stored = consultation?.editorial_overrides?.narrative_prose_review;
  return {
    required: fragments.length > 0 && text.length > 0,
    confirmed: stored?.texts_sha256 === textsSha256 && stored?.exclusions_sha256 === exclusionsSha,
    fragments,
    textsSha256,
    exclusionsSha256: exclusionsSha,
    label: fragments.length > 0 ? narrativeProseReviewLabel(fragments) : null,
  };
}

export function confirmNarrativeProseReview({ client, consultation, payload, now = new Date().toISOString() }) {
  const state = narrativeProseReviewState({ client, consultation, payload });
  if (!state.required) return fail("Aucune exclusion écrite en prose à relire dans ces textes.");
  return {
    ok: true,
    consultation: {
      ...consultation,
      editorial_overrides: {
        ...(consultation?.editorial_overrides || {}),
        narrative_prose_review: {
          texts_sha256: state.textsSha256,
          exclusions_sha256: state.exclusionsSha256,
          confirmed_at: now,
        },
      },
    },
  };
}

/**
 * Contrôle de publication des clients MGD, fail-closed et sans dérogation.
 * La preuve MGD ne couvre que le texte du plan : tout le reste de l'app est
 * contrôlé ici contre les exclusions explicites de l'anamnèse.
 *
 * - Contenus alimentaires (semaine, menus, recettes, rotation, Meal Prep,
 *   frigo hors « À limiter ») : chaque couple (ligne, aliment exclu) bloque,
 *   sauf confirmation d'Anissa encore valable pour ce repas, ce texte exact et
 *   ces exclusions (éditeur Semaine).
 * - Textes rédigés (titre, objectif, introduction, stratégie, compléments,
 *   parcours) : une mention dans une formulation d'éviction fermée (« sans
 *   arachides », « allergie aux arachides ») n'est pas un conflit — même règle
 *   que la clairance clinique ; toute autre mention bloque, à reformuler.
 * - Exclusions en prose : chaque repas doit avoir été vérifié par Anissa.
 *
 * @returns {string[]} messages bloquants (vide = rien à signaler)
 */
export function findPublicationFoodBlockers({ client, consultation, payload }) {
  const snapshot = exclusionSnapshot(client);
  if (!snapshot) {
    return ["Publication bloquée : les exclusions de la cliente sont indisponibles — recharge sa fiche avant de publier."];
  }
  const { exclusions, uninterpretable } = analyzeFoodExclusions(snapshot);
  const blockers = [];
  const add = (message) => { if (!blockers.includes(message)) blockers.push(message); };
  const confirmed = currentConfirmedConflicts(consultation, snapshot);
  const lines = readableFoodLines({
    week_meals: payload?.sections?.week_meals,
    rotation_data: payload?.sections?.rotation_data,
    fridge_data: payload?.sections?.fridge_data,
  });
  for (const { text, slot } of lines) {
    const textSha256 = sha256HexSync(text);
    for (const exclusion of matchingExclusions(text, exclusions)) {
      const accepted = confirmed.some((item) => item.exclusion === exclusion
        && item.textSha256 === textSha256
        && (slot ? item.slot === slot : true));
      if (!accepted) {
        add(`Publication bloquée : « ${text} » mentionne un aliment exclu (${exclusion}) — corrige dans Semaine, menus & recettes.`);
      }
    }
  }
  for (const [name, read] of NARRATIVE_SECTIONS) {
    const text = allStrings(read(payload)).join("\n");
    if (!text.trim()) continue;
    // Mêmes exclusions normalisées que pour les menus (singulier/pluriel,
    // « allergie aux », « sans », « et/ou ») ; éviction fermée tolérée.
    for (const term of findExclusionTermsPresent(text, exclusions)) {
      add(`Publication bloquée : ${name} mentionne un aliment exclu (${term}) — reformule ce texte avant de publier.`);
    }
  }
  const narrativeReview = narrativeProseReviewState({ client, consultation, payload });
  if (narrativeReview.required && !narrativeReview.confirmed) {
    add(`Publication bloquée : relis l’introduction, la stratégie et les compléments au regard de « ${narrativeReview.fragments.join(" », « ")} », puis coche la confirmation au-dessus de « Publier ».`);
  }
  if (uninterpretable.length > 0) {
    let checkedEverywhere = false;
    try {
      const model = buildWeekMenusModel({ client, consultation });
      checkedEverywhere = model.slots.length > 0
        && model.slots.every((slotModel) => slotModel.proseExclusions.length === 0);
    } catch {
      checkedEverywhere = false;
    }
    if (!checkedEverywhere) {
      for (const fragment of uninterpretable) {
        add(`Publication bloquée : exclusion non interprétable « ${fragment} » — ouvre Semaine, menus & recettes et coche la vérification pour chaque repas.`);
      }
    }
  }
  return blockers;
}

export const PROSE_EXCLUSIONS_CONFIRMATION = "J’ai vérifié ce repas au regard de cette exclusion";

/**
 * Anissa confirme avoir vérifié elle-même un repas au regard des exclusions
 * écrites en prose. Liée au contenu ET aux exclusions : toute modification de
 * l'un ou de l'autre annule la confirmation. Ce n'est pas une validation.
 */
export function confirmProseExclusionsChecked(state, slot, { now = new Date().toISOString() } = {}) {
  const snapshot = exclusionSnapshot(state.client);
  const prose = snapshot ? analyzeFoodExclusions(snapshot).uninterpretable : [];
  if (prose.length === 0) return fail("Aucune exclusion écrite en prose à vérifier pour ce repas.");
  const model = buildWeekMenusModel(state);
  const slotModel = findSlot(model, slot);
  if (!slotModel) return fail("Ce repas n’existe pas dans la semaine.");
  const review = readReview(state.consultation);
  return {
    ok: true,
    consultation: withReview(state.consultation, {
      ...review,
      slots: {
        ...review.slots,
        [slot]: {
          ...(review.slots[slot] || {}),
          prose_exclusions_checked: {
            content_sha256: slotModel.contentSha256,
            exclusions_sha256: hashOf(prose),
            confirmed_at: now,
          },
        },
      },
    }),
  };
}

export function validateSlot(state, slot, { now = new Date().toISOString() } = {}) {
  const model = buildWeekMenusModel(state);
  const slotModel = findSlot(model, slot);
  if (!slotModel) return fail("Ce repas n’existe pas dans la semaine.");
  if (slotModel.issues.length > 0) {
    return { ok: false, error: `${slotModel.label} ne peut pas encore être validé.`, issues: slotModel.issues };
  }
  const review = readReview(state.consultation);
  const nextReview = {
    ...review,
    slots: {
      ...review.slots,
      [slot]: {
        ...(review.slots[slot] || {}),
        content_sha256: slotModel.contentSha256,
        // Une validation vaut pour CES exclusions : en changer la retire.
        exclusions_sha256: exclusionsSha256(exclusionSnapshot(state.client)),
        validated_at: now,
      },
    },
  };
  return { ok: true, consultation: withReview(state.consultation, nextReview) };
}

/**
 * Prépare l'enregistrement : marque « enregistré, à valider » les seuls repas
 * modifiés. Un enregistrement n'est jamais une validation.
 */
export function markWeekMenusSaved({ client = null, previous, next, now = new Date().toISOString() }) {
  const before = buildWeekMenusModel({ client, consultation: previous });
  const after = buildWeekMenusModel({ client, consultation: next });
  const beforeBySlot = new Map(before.slots.map((slotModel) => [slotModel.slot, slotModel]));
  const review = readReview(next);
  const slots = { ...review.slots };
  const changes = [];
  for (const slotModel of after.slots) {
    const old = beforeBySlot.get(slotModel.slot);
    const poolChanged = !old || old.contentSha256 !== slotModel.contentSha256;
    const changedWeeks = poolChanged
      ? [1, 2, 3, 4]
      : slotModel.mains.map((main, index) => (main.id !== old.mains[index].id ? index + 1 : null)).filter(Boolean);
    if (poolChanged) {
      slots[slotModel.slot] = { ...(slots[slotModel.slot] || {}), saved_sha256: slotModel.contentSha256, saved_at: now };
    }
    // Confirmations dont le texte n'est plus dans ce repas : retirées à
    // l'enregistrement. Celles dont le texte existe toujours sont gardées et
    // rattachées au choix qui le porte désormais (identifiant éventuellement
    // décalé par le retrait d'une alternative au titre voisin).
    const confirmations = slots[slotModel.slot]?.food_conflicts_confirmed;
    if (Array.isArray(confirmations)) {
      const choiceIds = new Set(slotModel.choices.map((choice) => choice.id));
      const kept = slotModel.foodConflicts.length > 0 || exclusionSnapshot(client)
        ? confirmations.flatMap((entry) => {
          const owner = slotModel.foodConflicts.find((conflict) => conflict.field === entry?.field
            && conflict.textSha256 === entry?.text_sha256
            && conflict.exclusion === entry?.exclusion);
          return owner ? [{ ...entry, choice_id: owner.choiceId }] : [];
        })
        : confirmations.filter((entry) => choiceIds.has(entry?.choice_id));
      if (JSON.stringify(kept) !== JSON.stringify(confirmations)) {
        slots[slotModel.slot] = { ...slots[slotModel.slot], food_conflicts_confirmed: kept };
      }
    }
    if (changedWeeks.length > 0) {
      changes.push({ slot: slotModel.slot, label: slotModel.label, weeks: changedWeeks, reason: poolChanged ? "pool" : "mains" });
    }
  }
  return {
    consultation: withReview(next, { ...review, slots }),
    changes,
  };
}

/** Enregistre, avec la validation de la rotation, l'état exact relu par Anissa. */
export function recordRotationValidation(state, { now = new Date().toISOString() } = {}) {
  const model = buildWeekMenusModel(state);
  const review = readReview(state.consultation);
  const nextReview = {
    ...review,
    rotation: {
      slot_hashes: Object.fromEntries(model.slots.map((slotModel) => [slotModel.slot, slotModel.rotationSha256])),
      slot_pools: Object.fromEntries(model.slots.map((slotModel) => [slotModel.slot, slotModel.contentSha256])),
      slot_mains: Object.fromEntries(model.slots.map((slotModel) => [slotModel.slot, slotModel.mains.map((main) => main.id)])),
      validated_at: now,
    },
  };
  return withReview(state.consultation, nextReview);
}

/** Phrase précise pour chaque repas à revérifier après une modification. */
export function describeWeekMenuChange(change) {
  if (change.reason === "mains") {
    return `${change.label} : plat principal changé pour ${joinWeeks(change.weeks)}.`;
  }
  return `${change.label} : à revérifier dans ${joinWeeks(change.weeks)} — le réservoir de ce repas est partagé par les 4 semaines. Les autres repas ne changent pas.`;
}
